<script setup lang="ts">
import { i18n } from '@/i18n';
import OrderGroupInfoSection from '../../../corePagesModule/components/OrderGroup/OrderGroupInfoSection.vue';
import { IOrderGroup } from '@/coreDomainsModule/entities/IOrderGroup';
import EzTable from '@/corePagesModule/components/ezTable/EzTable.vue';
import EzTableRow from '@/corePagesModule/components/ezTable/EzTableRow.vue';
import EzHeader from '@/corePagesModule/components/ezTable/EzHeader.vue';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { Order } from '@/coreDomainsModule/entities/order';
import { onMounted, ref } from 'vue';
import { LoadingStatus, loadingStatusHelpers } from '@/corePagesModule/components/ezTable/types';
import { OrderGroupService } from '@/orderTableModule/domains/service/orderGroupService';
import { diContainer } from '@/coreContainer/container';
import { initPagingOptions } from '@/coreDomainsModule/paging';

const t = i18n.t;

const items = ref<Order[]>([]);
const loadingStatus = ref<LoadingStatus>({ type: 'loading' });

const props = withDefaults(
  defineProps<{
    changedETA: number;
    targetOrderGroup: IOrderGroup;
  }>(),
  {}
);

const gridTemplateColumns: string[] = ['124px', '124px', '124px', '124px', '124px', '124px', '80px', '124px'];

/**
 * fetchとinitialization
 */
const fetchAndInitItem = async () => {
  const { loadingStatus: fetchLoadingStatus, items: fetchItems } = await _fetchOrderLinkedTargetOrderGroup(
    props.targetOrderGroup.orderGroupId
  );
  loadingStatus.value = fetchLoadingStatus;
  if (!loadingStatusHelpers.isError(fetchLoadingStatus) && fetchItems) {
    items.value = fetchItems;
  }
};

/**
 * 対象の発注グループに紐づく発注を取得する
 * @param orderGroupId - 対象の発注グループID
 */
const _fetchOrderLinkedTargetOrderGroup = async (orderGroupId: string): Promise<{ loadingStatus: LoadingStatus; items?: Order[] }> => {
  const useFilterItems = [OrderGroupService.getFilterContentItem(orderGroupId)];
  const result = await diContainer.getUnorderedArrivalUseCase.Execute(false, initPagingOptions(100), 'orderId', true, useFilterItems, {
    ApprovedAwaitingDeliveryDate: true,
    QuantityConfirmed: true,
    DeliveryDateConfirmed: true,
    PartialDelivery: true,
    Confirmed: true,
    Delivered: true,
    DeliveryDelayed: true,
    ApprovalRequested: false,
  });
  let loadingStatus: LoadingStatus = { type: 'loading' };
  switch (result.status.type) {
    case 'success':
      if ((result.status.data.pagingOptions?.returnCount ?? 0) === 0) {
        loadingStatus = { type: 'zero-match' };
      } else {
        loadingStatus = { type: 'success' };
      }
      return {
        loadingStatus,
        items: result.status.data.result,
      };
    case 'error':
      loadingStatus = {
        type: 'error',
        errorMessage: '取得に失敗しました',
      };
      return {
        loadingStatus,
      };
  }
};

onMounted(async () => {
  await fetchAndInitItem();
});
</script>
<template>
  <div class="bulk-change-eta__container">
    <h2 class="bulk-change-eta__text">{{ t('発注グループ設定') }}</h2>
    <div class="eta__container">
      <div class="bulk-change-eta__text">{{ t('納期') }}</div>
      <div class="eta__body">
        <SvgLoader name="calendar_today_gray2x" />
        <div class="bulk-change-eta__text">
          {{ DateService.convertIYearMonthDayToYYYYMMDDWithSlash(DateService.convertUnixTimeToIDate(props.changedETA / 1000)) }}
        </div>
      </div>
    </div>
    <OrderGroupInfoSection
      :order-group-name="props.targetOrderGroup.orderGroupName"
      :number-of-order="props.targetOrderGroup.linkedOrderCount"
      :upper-limit-quantity="props.targetOrderGroup.upperLimitQuantity"
      :total-amount="props.targetOrderGroup.totalLinkedOrderAmount"
      :has-background="false"
    />
    <div class="orders__container">
      <div class="bulk-change-eta__text">{{ t('発注グループ詳細') }}</div>
      <EzTable
        :grid-template-columns="gridTemplateColumns"
        sticky-header
        max-height="300px"
        :loading-status="loadingStatus"
        @retry="fetchAndInitItem"
      >
        <template #header>
          <EzHeader class="orders__header">
            {{ t('置場コード') }}
          </EzHeader>
          <EzHeader class="orders__header">
            {{ t('置場名') }}
          </EzHeader>
          <EzHeader class="orders__header">
            {{ t('仕入先コード') }}
          </EzHeader>
          <EzHeader class="orders__header">
            {{ t('仕入先名') }}
          </EzHeader>
          <EzHeader class="orders__header">
            {{ t('商品コード') }}
          </EzHeader>
          <EzHeader class="orders__header">
            {{ t('商品名') }}
          </EzHeader>
          <EzHeader class="orders__header">
            {{ t('容量') }}
          </EzHeader>
          <EzHeader class="orders__header">
            {{ t('発注数量') }}
          </EzHeader>
        </template>
        <template #body>
          <EzTableRow
            v-for="(order, index) in items"
            :id="`orders-row-${index}`"
            :key="order.orderId"
            :grid-template-columns="gridTemplateColumns"
            class="orders__row"
          >
            <EzCell class="orders__data">
              {{ order.warehouseNumber }}
            </EzCell>
            <EzCell class="orders__data">
              {{ order.warehouse }}
            </EzCell>
            <EzCell class="orders__data">
              {{ order.supplierNumber }}
            </EzCell>
            <EzCell class="orders__data">
              {{ order.supplierName }}
            </EzCell>
            <EzCell class="orders__data">
              {{ order.businessNumber }}
            </EzCell>
            <EzCell class="orders__data">
              {{ order.businessName }}
            </EzCell>
            <EzCell class="orders__data">
              {{ order.package }}
            </EzCell>
            <EzCell class="orders__data">
              {{ order.orderAmount.addComma() }}
            </EzCell>
          </EzTableRow>
        </template>
      </EzTable>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.bulk-change-eta {
  &__container {
    padding: 16px;
    display: grid;
    gap: 16px;
    grid-template-rows: repeat(4, auto);
    * {
      line-height: 1;
    }
    border-radius: 8px;
    background-color: $semanticPrimarySubdued;

    h2 {
      font-weight: 700;
    }
  }

  &__text {
    font-size: 16px;
  }
}

.eta {
  &__container {
    display: grid;
    grid-template-rows: auto auto;
    gap: 8px;
  }
  &__body {
    width: 100%;
    padding: 6px;
    background-color: white;
    border: 1px solid $borderDefault;
    border-radius: 4px;

    display: grid;
    grid-template-columns: max-content max-content;
    justify-content: center;
    align-items: center;
    gap: 10px;
  }
}

.orders {
  &__container {
    display: grid;
    grid-template-rows: auto auto;
    gap: 4px;
  }
  &__header {
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 700;
    line-height: 14px;
    color: $textDefault;
    padding: 11px 0;
    white-space: nowrap;
  }
  &__data {
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 14px;
    color: $textSecondary;
    padding: 8px 0;
  }
}
</style>
